import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography,
  Tab, Tabs, IconButton, Alert, CircularProgress, Chip, FormControl, InputLabel,
  Select, MenuItem, Snackbar,
} from '@mui/material';
import {
  Close as CloseIcon, Email as EmailIcon, WhatsApp as WhatsAppIcon,
  Send as SendIcon, AttachFile as AttachIcon, Add as AddIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../config/environment';

interface SendDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  documentType: string;
  documentId: string;
  patientId?: string;
  patientEmail?: string;
  patientPhone?: string;
  patientWhatsapp?: string;
  patientName?: string;
  documentLabel?: string;
}

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  document_type: string;
}

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  prescription: 'Receituário',
  certificate: 'Atestado',
  referral: 'Encaminhamento',
  budget: 'Orçamento',
  receipt: 'Recibo',
  evolution: 'Evolução Clínica',
  nfse: 'Nota Fiscal',
  report: 'Relatório',
};

const SendDocumentDialog: React.FC<SendDocumentDialogProps> = ({
  open, onClose, documentType, documentId, patientId,
  patientEmail, patientPhone, patientWhatsapp, patientName, documentLabel,
}) => {
  const [tab, setTab] = useState(0);
  const [sending, setSending] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Email state
  const [emailTo, setEmailTo] = useState('');
  const [emailExtraRecipients, setEmailExtraRecipients] = useState<string[]>([]);
  const [emailNewRecipient, setEmailNewRecipient] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [customMessage, setCustomMessage] = useState('');

  // WhatsApp state
  const [waPhone, setWaPhone] = useState('');
  const [waCaption, setWaCaption] = useState('');

  // Resolved patient name for display
  const [resolvedName, setResolvedName] = useState('');

  useEffect(() => {
    if (open) {
      setEmailExtraRecipients([]);
      setEmailNewRecipient('');
      setCustomMessage('');
      setWaCaption('');
      setTemplateId('');
      setSending(false);
      setResolvedName(patientName || '');
      loadTemplates();

      if (patientId) {
        loadPatientContactData(patientId);
      } else {
        setEmailTo(patientEmail || '');
        setWaPhone(patientWhatsapp || patientPhone || '');
      }
    }
  }, [open, patientId, patientEmail, patientPhone, patientWhatsapp, documentType]);

  const loadPatientContactData = async (pid: string) => {
    try {
      const { data } = await axios.get(getApiUrl(`/clinic/patients/${pid}`));
      const patient = data?.data || data;
      if (patient) {
        setEmailTo(patient.email || patientEmail || '');
        setWaPhone(patient.whatsapp || patient.phone || patientWhatsapp || patientPhone || '');
        if (patient.full_name) setResolvedName(patient.full_name);
      }
    } catch {
      setEmailTo(patientEmail || '');
      setWaPhone(patientWhatsapp || patientPhone || '');
    }
  };

  const loadTemplates = async () => {
    try {
      const { data } = await axios.get(getApiUrl(`/clinic/document-templates?document_type=${documentType}`));
      setTemplates(data?.data || []);
    } catch { /* ignore */ }
  };

  const allEmails = [emailTo, ...emailExtraRecipients].filter(e => e.trim());

  const handleAddRecipient = () => {
    const email = emailNewRecipient.trim();
    if (email && email.includes('@') && !emailExtraRecipients.includes(email) && email !== emailTo) {
      setEmailExtraRecipients([...emailExtraRecipients, email]);
      setEmailNewRecipient('');
    }
  };

  const handleRemoveRecipient = (email: string) => {
    setEmailExtraRecipients(emailExtraRecipients.filter(e => e !== email));
  };

  const handleSendEmail = async () => {
    if (allEmails.length === 0) return;
    setSending(true);
    try {
      await axios.post(getApiUrl('/clinic/documents/send-email'), {
        type: documentType,
        id: documentId,
        emails: allEmails,
        templateId: templateId || undefined,
        customMessage: customMessage || undefined,
      });
      setSnackbar({ open: true, message: 'Documento enviado por email com sucesso!', severity: 'success' });
      setTimeout(onClose, 1500);
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Erro ao enviar por email', severity: 'error' });
    } finally { setSending(false); }
  };

  const normalizePhone = (phone: string): string => {
    const clean = phone.replace(/\D/g, '');
    if (clean.startsWith('55') && clean.length >= 12) return clean;
    if (clean.length === 11 || clean.length === 10) return '55' + clean;
    return clean;
  };

  const handleSendWhatsApp = async () => {
    if (!waPhone.trim()) return;
    setSending(true);
    try {
      const phoneToSend = normalizePhone(waPhone.trim());
      await axios.post(getApiUrl('/clinic/documents/send-whatsapp'), {
        type: documentType,
        id: documentId,
        phone: phoneToSend,
        caption: waCaption || undefined,
      });
      setSnackbar({ open: true, message: 'Documento enviado via WhatsApp com sucesso!', severity: 'success' });
      setTimeout(onClose, 1500);
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Erro ao enviar via WhatsApp', severity: 'error' });
    } finally { setSending(false); }
  };

  const typeLabel = documentLabel || DOCUMENT_TYPE_LABELS[documentType] || documentType;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb', pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SendIcon sx={{ color: '#14b8a6' }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>Enviar Documento</Typography>
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                {typeLabel}{resolvedName ? ` - ${resolvedName}` : ''}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} disabled={sending}><CloseIcon /></IconButton>
        </DialogTitle>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth"
            sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 48 } }}>
            <Tab icon={<EmailIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Email" />
            <Tab icon={<WhatsAppIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="WhatsApp" />
          </Tabs>
        </Box>

        <DialogContent sx={{ p: 3, minHeight: 280 }}>
          {tab === 0 && (
            <Box>
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }} icon={<AttachIcon />}>
                O documento será enviado como <strong>PDF em anexo</strong> no email.
              </Alert>

              <TextField
                fullWidth label="Email do destinatário *" type="email"
                value={emailTo} onChange={(e) => setEmailTo(e.target.value)}
                placeholder="email@exemplo.com" size="small" sx={{ mb: 2 }}
              />

              {emailExtraRecipients.length > 0 && (
                <Box sx={{ mb: 2, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {emailExtraRecipients.map(e => (
                    <Chip key={e} label={e} size="small" onDelete={() => handleRemoveRecipient(e)}
                      sx={{ fontSize: '0.75rem' }} />
                  ))}
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth size="small" placeholder="Adicionar outro email..."
                  value={emailNewRecipient} onChange={(e) => setEmailNewRecipient(e.target.value)}
                  onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddRecipient(); } }}
                />
                <IconButton size="small" onClick={handleAddRecipient} sx={{ color: '#14b8a6' }}>
                  <AddIcon />
                </IconButton>
              </Box>

              {templates.length > 0 && (
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Template de email</InputLabel>
                  <Select value={templateId} onChange={(e) => setTemplateId(e.target.value)} label="Template de email">
                    <MenuItem value="">Padrão</MenuItem>
                    {templates.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                  </Select>
                </FormControl>
              )}

              <TextField
                fullWidth multiline rows={2} size="small"
                label="Mensagem personalizada (opcional)"
                placeholder="Adicione uma mensagem que será incluída no corpo do email..."
                value={customMessage} onChange={(e) => setCustomMessage(e.target.value)}
              />
            </Box>
          )}

          {tab === 1 && (
            <Box>
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }} icon={<AttachIcon />}>
                O documento será enviado como <strong>PDF no WhatsApp</strong> do paciente.
              </Alert>

              <TextField
                fullWidth label="Número do WhatsApp *"
                value={waPhone} onChange={(e) => setWaPhone(e.target.value)}
                placeholder="(31) 99999-9999" size="small" sx={{ mb: 2 }}
                helperText="DDD + número do celular"
              />

              <TextField
                fullWidth multiline rows={2} size="small"
                label="Legenda (opcional)"
                placeholder="Mensagem que acompanha o documento..."
                value={waCaption} onChange={(e) => setWaCaption(e.target.value)}
              />

              <Alert severity="warning" sx={{ mt: 2, borderRadius: 2, fontSize: '0.8rem' }}>
                É necessário ter uma instância WhatsApp ativa e conectada no sistema.
              </Alert>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2.5, borderTop: '1px solid #e5e7eb', gap: 1 }}>
          <Button onClick={onClose} disabled={sending} sx={{ color: '#6b7280', textTransform: 'none' }}>Cancelar</Button>
          {tab === 0 ? (
            <Button
              variant="contained" onClick={handleSendEmail}
              disabled={sending || allEmails.length === 0}
              startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <EmailIcon />}
              sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', fontWeight: 600 }}
            >
              {sending ? 'Enviando...' : 'Enviar por Email'}
            </Button>
          ) : (
            <Button
              variant="contained" onClick={handleSendWhatsApp}
              disabled={sending || !waPhone.trim()}
              startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <WhatsAppIcon />}
              sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#1fba59' }, textTransform: 'none', fontWeight: 600 }}
            >
              {sending ? 'Enviando...' : 'Enviar via WhatsApp'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SendDocumentDialog;
